import { graphql } from '@apollo/client/react/hoc';

import FundProfileShowPageQuery from 'buyside/graphql/queries/FundProfileShowPage';
import AcceptFundDisclaimerMutation from 'buyside/graphql/mutations/AcceptFundDisclaimer';
import UpdateFundProfileBackgroundImageMutation from 'buyside/graphql/mutations/UpdateFundProfileBackgroundImage';
import PublishFundProfileMutation from 'buyside/graphql/mutations/PublishFundProfile';
import CreateFundProfileMessageMutation from 'buyside/graphql/mutations/CreateFundProfileMessage';
import CreateAuditRecordMutation from 'buyside/graphql/mutations/CreateAuditRecord';
import UpdateFundProfileMutation from 'buyside/graphql/mutations/UpdateFundProfile';
import FundProfileShowPage from 'buyside/components/presentation/FundProfileShowPage/fundProfileShowPage';

import { flowRight as compose } from 'buyside/util/lodashAlt';

const withData = graphql(FundProfileShowPageQuery, {
  props({ data: { loading, currentUser, fundProfile } }) {
    return { loading, currentUser, fundProfile };
  },
  options: ({ employerProfileId }) => ({ variables: { employerProfileId } })
});

const withAcceptFundDisclaimer = graphql(AcceptFundDisclaimerMutation, {
  props: ({ mutate }) => ({
    acceptFundDisclaimer: () => mutate()
  })
});

const withUpdateBackgroundImage = graphql(UpdateFundProfileBackgroundImageMutation, {
  props: ({ mutate, ownProps: { employerProfileId } }) => ({
    updateBackgroundImage: ({ cropData, imageData, removeImage }) => {
      return mutate({
        variables: {
          employerProfileId,
          imageData,
          cropData: JSON.stringify(cropData),
          removeImage: removeImage
        }
      });
    }
  })
});

const withPublishProfile = graphql(PublishFundProfileMutation, {
  props: ({ mutate, ownProps: { employerProfileId } }) => ({
    publishProfile: () => mutate({ variables: { employerProfileId } })
  })
});

const withSendMessage = graphql(CreateFundProfileMessageMutation, {
  props: ({ mutate, ownProps: { employerProfileId } }) => ({
    sendMessage: body => {
      return mutate({
        variables: { employerProfileId, body }
      });
    }
  })
});

const withCreateAuditRecord = graphql(CreateAuditRecordMutation, {
  props: ({ mutate, ownProps: { employerProfileId } }) => ({
    createAuditRecord: (auditableId, auditableType, action) => {
      return mutate({
        variables: {
          auditableId,
          auditableType,
          action,
          parentId: employerProfileId,
          parentType: 'EmployerProfile'
        }
      });
    }
  })
});

const withUpdateFundProfile = graphql(UpdateFundProfileMutation, {
  props: ({ mutate }) => ({
    updateFundProfile: ({ fundProfile, section }) => {
      const {
        fundType,
        strategy,
        focus,
        location,
        fundManager,
        aum,
        foundingDate,
        description,
        imageData,
        cropData,
        removeImage,
        executives,
        firmReferences,
        sectorExpertise,
        geographicExpertise,
        assetClassExpertise,
        subsectorExpertise,
        tagNames,
        returns,
        returnsUpdatedDate,
        returnsDocuments,
        featuredResearch,
        featuredWhitePaperResearch,
        holdings,
        holdingsDocuments,
        marketingMaterials,
        presses,
        sharpeRatio,
        beta,
        turnoverRatio,
        metricsDocuments,
        managementFee,
        incentiveFee,
        hurdleRate,
        lockUp,
        highwaterMark,
        seedDeal,
        foundersShares,
        investorLevelGates,
        feeStructureDocuments,
        dueDiligenceDocuments,
        serviceProviders,
        notes
      } = fundProfile;
      return mutate({
        variables: {
          employerProfileId: fundProfile.id,
          section,
          fundType,
          strategy,
          focus,
          location,
          fundManager,
          aum: parseInt(aum),
          foundingDate,
          description,
          imageData,
          cropData,
          removeImage,
          executives: JSON.stringify(executives),
          firmReferences: JSON.stringify(firmReferences),
          sectorExpertise,
          geographicExpertise,
          assetClassExpertise,
          subsectorExpertise,
          tagNames,
          returns: JSON.stringify(returns),
          returnsUpdatedDate,
          returnsDocuments: JSON.stringify(returnsDocuments),
          ideaIds: featuredResearch.map(idea => idea.id),
          whitePaperIds: featuredWhitePaperResearch.map(wp => wp.id),
          holdings: JSON.stringify(holdings),
          holdingsDocuments: JSON.stringify(holdingsDocuments),
          marketingMaterials: JSON.stringify(marketingMaterials),
          presses: JSON.stringify(presses),
          sharpeRatio: parseFloat(sharpeRatio),
          beta: parseFloat(beta),
          turnoverRatio: parseFloat(turnoverRatio),
          metricsDocuments: JSON.stringify(metricsDocuments),
          managementFee: parseFloat(managementFee),
          incentiveFee: parseFloat(incentiveFee),
          hurdleRate: parseFloat(hurdleRate),
          lockUp: parseInt(lockUp),
          highwaterMark,
          seedDeal,
          foundersShares,
          investorLevelGates,
          feeStructureDocuments: JSON.stringify(feeStructureDocuments),
          dueDiligenceDocuments: JSON.stringify(dueDiligenceDocuments),
          serviceProviders: JSON.stringify(serviceProviders),
          notes
        },
        update: (proxy, { data: { updateFundProfile } }) => {
          if (!updateFundProfile.employerProfile) return;

          const data = proxy.readQuery({
            query: FundProfileShowPageQuery,
            variables: { employerProfileId: fundProfile.id }
          });
          proxy.writeQuery({
            query: FundProfileShowPageQuery,
            data: { currentUser: data.currentUser, fundProfile: updateFundProfile.employerProfile }
          });
        }
      });
    }
  })
});

const FundProfileShowPageContainer = compose(
  withData,
  withAcceptFundDisclaimer,
  withUpdateBackgroundImage,
  withPublishProfile,
  withSendMessage,
  withCreateAuditRecord,
  withUpdateFundProfile
)(FundProfileShowPage);

export default FundProfileShowPageContainer;
